import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { contributionsApi } from "@/api/contributions.api";
import paymentsApi from "@/api/payments.api";
import type { UserStats } from "@/components/stats";

/** The contributor's figures and payout situation, both computed by the server. */
export function useMyContributions(userId: string | undefined) {
  const queryClient = useQueryClient();
  const stats = useQuery({
    queryKey: ["my-contributions", "stats", userId],
    queryFn: async () => (await contributionsApi.getUserStats(userId!)) as UserStats,
    enabled: Boolean(userId),
  });
  const situation = useQuery({
    queryKey: ["my-contributions", "payout-situation", userId],
    queryFn: () => paymentsApi.getContributorPayoutSituation(Number(userId)),
    enabled: Boolean(userId),
    // A contributor without payout data yet still gets the page.
    retry: false,
  });

  // "Refresh" skips the server cache, so a review done a minute ago shows up.
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    if (!userId) return;
    setRefreshing(true);
    try {
      const fresh = (await contributionsApi.getUserStats(userId, { fresh: true })) as UserStats;
      queryClient.setQueryData(["my-contributions", "stats", userId], fresh);
    } catch {
      await stats.refetch();
    } finally {
      await situation.refetch();
      setRefreshing(false);
    }
  }, [userId, queryClient, stats, situation]);

  return { stats, situation, refresh, refreshing };
}
